import LogService from '../services/LogService';
import ILog from '../types/Log';
import IPaciente from '../types/Paciente';
import * as constantes from './Constantes';

const logOut = () => {
    localStorage.removeItem(constantes.LS_profile);
    localStorage.removeItem(constantes.LS_config);
    window.dispatchEvent(new Event('popstate'));
};

const formatMonthYear = (date: string) => {
    try {
        var d = new Date(date),
        month = '' + d.getMonth(),
        year = d.getFullYear();

    
        return `${constantes.monthNames[parseInt(month)]}, ${year}`;
    } catch (e: any) {
        const error: ILog = { method: 'Utilities.formatMonthYear', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatTime = (date: string) => {
    try {
        var d = new Date(date),
        hours = '' + (d.getHours()),
        minutes = '' + d.getMinutes();

        if (hours.length < 2) 
            hours = '0' + hours;

        if (minutes.length < 2) 
            minutes = '0' + minutes;
    
        return `${hours}:${minutes}`;
    } catch (e: any) {
        const error: ILog = { method: 'Utilities.formatTime', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const isNullEmptyUndefined = (value: any) => {
    try {
        if (value === null) return true;
        if (value === undefined) return true;
        if (value === '') return true;
    
        return false;
    } catch (e: any) {
        const error: ILog = { method: 'Utilities.isNullEmptyUndefined', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const validateSpanishID = (id: string) => {
    try {
        if (constantes.DNI_REGEX.test(id))
            return true;
        if (constantes.NIE_REGEX.test(id))
            return true;
        if (constantes.CIF_REGEX.test(id))
            return true;

        return false;
    } catch (e: any) {
        const error: ILog = { method: 'Utilities.validateSpanishID', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return false;
    }
};

// Identificador fiscal de la clínica (nif de datosFacturacion). Fuera de España no hay
// un formato único y estable que podamos validar con certeza, así que se aplica una
// comprobación genérica de longitud/caracteres en vez de intentar adivinar el formato exacto.
const validateTaxId = (id: string, pais: string) => {
    try {
        if (pais === 'ES') return validateSpanishID(id);
        return constantes.TAX_ID_GENERICO_REGEX.test(id);
    } catch (e: any) {
        const error: ILog = { method: 'Utilities.validateTaxId', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return false;
    }
};

const validatePostalCode = (cp: string, pais: string) => {
    try {
        if (pais === 'ES') return /^\d{5}$/.test(cp);
        return constantes.CODIGO_POSTAL_GENERICO_REGEX.test(cp);
    } catch (e: any) {
        const error: ILog = { method: 'Utilities.validatePostalCode', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return false;
    }
};

const getNifLabel = (pais: string): string => {
    return constantes.paises.find(p => p.id === pais)?.nifLabel || 'Identificación fiscal';
};

// Igual que getNifLabel pero restringido a personas físicas (p.ej. el tutor legal de
// un paciente): no debe ofrecer un identificador de empresa (RUC/CIF), solo el
// equivalente a un DNI personal.
const getPersonNifLabel = (pais: string): string => {
    return constantes.paises.find(p => p.id === pais)?.personNifLabel || 'Documento de identidad';
};

// Etiqueta a mostrar para un documento de identidad ya guardado (paciente, tutor,
// responsable...). En España conserva la detección exacta de siempre (DNI/NIE/CIF/
// Pasaporte, vía getSpanichIDType) para no cambiar nada a los usuarios españoles; fuera
// de España (donde no hay forma de distinguir el tipo exacto por formato) usa la
// etiqueta general (getNifLabel) o restringida a persona física (getPersonNifLabel).
const getIdLabel = (id: string | undefined | null, pais: string, personOnly = false): string => {
    if (pais === 'ES') {
        const tipo = getSpanichIDType(id || '');
        if (tipo) return tipo;
    } else if (id && constantes.Pasaporte_REGEX.test(id)) {
        return 'Pasaporte';
    }
    return personOnly ? getPersonNifLabel(pais) : getNifLabel(pais);
};

const validatePassport = (id: string) => {
    try {
        if (constantes.Pasaporte_REGEX.test(id))
            return true;
        
            return false;
    } catch (e: any) {
        const error: ILog = { method: 'Utilities.validatePassport', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return false;
    }
};

const getSpanichIDType = (id: string) => {
    try {
        if (constantes.DNI_REGEX.test(id))
            return "DNI";
        if (constantes.NIE_REGEX.test(id))
            return "NIE";
        if (constantes.CIF_REGEX.test(id))
            return "CIF";
        if (constantes.Pasaporte_REGEX.test(id))
            return "Pasaporte";

        return "";
    } catch (e: any) {
        const error: ILog = { method: 'Utilities.getSpanichIDType', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return "";
    }
};

const compareDates = (date1: Date, date2: Date): boolean => {
    try {
    const [year1, month1, day1] = [ date1.getFullYear(), date1.getMonth(), date1.getDate() ];
    const [year2, month2, day2] = [ date2.getFullYear(), date2.getMonth(), date2.getDate() ];

    if (year1 === year2 && month1 === month2 && day1 === day2) return true;
    else return false;

    } catch (e: any) {
        const error: ILog = { method: 'Utilities.compareDates', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return false;
    }
};

const formatAmount = (amount: number): string => {
    return amount.toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

const isDateBeforeOrEqual = (date1: Date, date2: Date): boolean => {

    try {
        const date1NoTime = new Date(new Date(date1).getFullYear(), new Date(date1).getMonth(), new Date(date1).getDate());
        const date2NoTime = new Date(new Date(date2).getFullYear(), new Date(date2).getMonth(), new Date(date2).getDate());
        return date1NoTime.getTime() <= date2NoTime.getTime();
    } catch (e: any) {
        const error: ILog = { method: 'Utilities.isDateBeforeOrEqual', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return false;
    }
};

const normalizeText = (text: string = ""): string => {
    return text
        .toLowerCase()
        .normalize("NFD") // separar acentos
        .replace(/[\u0300-\u036f]/g, "") // quitar acentos
        .replace(/\s+/g, ""); // quitar espacios
};

const filterPacientesAutocomplete = (pacientes: IPaciente[], inputValue: string): IPaciente[] => {
    const normalizedInput = Utilities.normalizeText(inputValue);

    return pacientes.filter(paciente => {
        const nombreCompleto = Utilities.normalizeText(`${paciente.nombre} ${paciente.apellidos}`);
        return nombreCompleto.includes(normalizedInput);
    });
};

interface IResolverPacienteFactura {
    pacienteId?: string;
    receptor: { nif?: string; nombre: string };
}

const resolverPacienteFactura = (
    pacientes: IPaciente[],
    factura: IResolverPacienteFactura
): IPaciente | null => {
    if (factura.pacienteId) {
        const paciente = pacientes.find(p => p._id === factura.pacienteId) || null;
        if (paciente) return paciente;
    }

    // Sin NIF no podemos buscar con garantías: mejor no mostrar nada que adivinar un paciente
    // cualquiera (varios pacientes sin DNI harían match por igual con undefined === undefined).
    if (!factura.receptor?.nif) return null;

    return pacientes.find(p => p.DNI === factura.receptor?.nif) || null;
};

const Utilities = {
    logOut,
    formatMonthYear,
    formatTime,
    isNullEmptyUndefined,
    validateSpanishID,
    validateTaxId,
    validatePostalCode,
    getNifLabel,
    getPersonNifLabel,
    getIdLabel,
    getSpanichIDType,
    validatePassport,
    compareDates,
    formatAmount,
    isDateBeforeOrEqual,
    normalizeText,
    filterPacientesAutocomplete,
    resolverPacienteFactura
};

export default Utilities;